import { useState, useEffect } from 'react'
import {
  X,
  User,
  Shield,
  Palette,
  Mic,
  Bell,
  Keyboard,
  Globe,
  Cpu,
  LogOut,
  ChevronRight
} from 'lucide-react'
import { useToast } from '../hooks/useToast'

// Import tab components
import MyAccountTab from './settings/MyAccountTab'
import ProfileTab from './settings/ProfileTab'
import PrivacyTab from './settings/PrivacyTab'
import VoiceVideoTab from './settings/VoiceVideoTab'
import AppearanceTab from './settings/AppearanceTab'
import NotificationsTab from './settings/NotificationsTab'
import KeybindsTab from './settings/KeybindsTab'
import AdvancedTab from './settings/AdvancedTab'
import StorageSettings from './settings/StorageSettings'
import LanguageTab from './settings/LanguageTab'

/**
 * GJYL Settings Modal - Modern Dashboard Aesthetic
 * Full-screen modal with icon-based navigation sidebar
 */
export default function SettingsModal({ isOpen, onClose, user, profile, onUpdateProfile, onSignOut }) {
  const toast = useToast()
  const [activeTab, setActiveTab] = useState('audio-video')

  // Close on ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) {
      window.addEventListener('keydown', handleEsc)
      return () => window.removeEventListener('keydown', handleEsc)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  const menuGroups = [
    {
      title: 'User',
      items: [
        { id: 'account', label: 'My Account', icon: User },
        { id: 'privacy', label: 'Privacy & Safety', icon: Shield },
        { id: 'profile', label: 'Profile Customization', icon: Palette },
      ]
    },
    {
      title: 'App Settings',
      items: [
        { id: 'appearance', label: 'Appearance', icon: Palette },
        { id: 'audio-video', label: 'Audio & Video', icon: Mic },
        { id: 'notifications', label: 'Notifications & Sounds', icon: Bell },
        { id: 'keybinds', label: 'Keybinds', icon: Keyboard },
        { id: 'language', label: 'Language', icon: Globe },
      ]
    },
    {
      title: 'System',
      items: [
        { id: 'advanced', label: 'Advanced', icon: Cpu },
        { id: 'logout', label: 'Log Out', icon: LogOut, danger: true },
      ]
    },
  ]

  const renderContent = () => {
    switch (activeTab) {
      case 'account':
        return <MyAccountTab user={user} profile={profile} onUpdateProfile={onUpdateProfile} />
      case 'profile':
        return <ProfileTab profile={profile} onUpdateProfile={onUpdateProfile} />
      case 'privacy':
        return <PrivacyTab profile={profile} onUpdateProfile={onUpdateProfile} />
      case 'appearance':
        return <AppearanceTab profile={profile} onUpdateProfile={onUpdateProfile} />
      case 'audio-video':
        return <VoiceVideoTab profile={profile} onUpdateProfile={onUpdateProfile} />
      case 'notifications':
        return <NotificationsTab profile={profile} onUpdateProfile={onUpdateProfile} />
      case 'keybinds':
        return <KeybindsTab />
      case 'language':
        return <LanguageTab profile={profile} onUpdateProfile={onUpdateProfile} />
      case 'advanced':
        return <AdvancedTab profile={profile} onUpdateProfile={onUpdateProfile} />
      default:
        return <VoiceVideoTab profile={profile} onUpdateProfile={onUpdateProfile} />
    }
  }

  const handleItemClick = (item) => {
    if (item.id === 'logout') {
      onSignOut()
      onClose()
    } else {
      setActiveTab(item.id)
    }
  }

  const getCurrentLabel = () => {
    for (const group of menuGroups) {
      const item = group.items.find(i => i.id === activeTab)
      if (item) return item.label
    }
    return 'Settings'
  }

  // Handle backdrop click to close
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-8"
      onClick={handleBackdropClick}
    >
      {/* Modal Container - Glassmorphism */}
      <div className="w-full max-w-5xl h-[85vh] max-h-[800px] flex rounded-2xl overflow-hidden shadow-2xl shadow-black/50 border border-white/10 bg-gray-900/80 backdrop-blur-xl">
        {/* Left Navigation Sidebar */}
        <div className="w-64 bg-gray-900/60 backdrop-blur-md border-r border-white/5 flex flex-col">
        {/* Logo/Header */}
        <div className="h-16 flex items-center px-6 border-b border-gray-800/50">
          <span className="text-xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
            GJYL Settings
          </span>
        </div>

        {/* Navigation Groups */}
        <div className="flex-1 overflow-y-auto py-4 px-3">
          {menuGroups.map((group, groupIndex) => (
            <div key={group.title} className={groupIndex > 0 ? 'mt-6' : ''}>
              {/* Group Title */}
              <div className="px-3 mb-2">
                <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                  {group.title}
                </span>
              </div>

              {/* Menu Items */}
              <div className="space-y-1">
                {group.items.map((item) => {
                  const Icon = item.icon
                  const isActive = activeTab === item.id
                  const isDanger = item.danger

                  return (
                    <button
                      key={item.id}
                      onClick={() => handleItemClick(item)}
                      className={`
                        w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
                        transition-all duration-200 group relative
                        ${isActive
                          ? 'bg-indigo-600/20 text-white'
                          : isDanger
                            ? 'text-red-400 hover:bg-red-500/10 hover:text-red-300'
                            : 'text-gray-400 hover:bg-gray-800/60 hover:text-white'
                        }
                      `}
                    >
                      {/* Active indicator - glowing left border */}
                      {isActive && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-indigo-500 rounded-r-full shadow-[0_0_10px_rgba(99,102,241,0.5)]" />
                      )}

                      {/* Icon */}
                      <div className={`
                        w-9 h-9 rounded-lg flex items-center justify-center
                        transition-colors duration-200
                        ${isActive
                          ? 'bg-indigo-500/20 text-indigo-400'
                          : isDanger
                            ? 'bg-red-500/10 text-red-400 group-hover:bg-red-500/20'
                            : 'bg-gray-800/50 text-gray-400 group-hover:bg-gray-700/50 group-hover:text-white'
                        }
                      `}>
                        <Icon className="w-5 h-5" />
                      </div>

                      {/* Label */}
                      <span className="flex-1 text-left text-sm font-medium">
                        {item.label}
                      </span>

                      {/* Arrow indicator for active */}
                      {isActive && (
                        <ChevronRight className="w-4 h-4 text-indigo-400" />
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Version info at bottom */}
        <div className="p-3 border-t border-white/5">
          <div className="text-xs text-gray-500 text-center">
            GJYL v3.0.0
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col bg-gray-950/40">
          {/* Header */}
          <div className="h-14 flex items-center justify-between px-6 border-b border-white/5">
            <h2 className="text-lg font-semibold text-white">
              {getCurrentLabel()}
            </h2>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full flex items-center justify-center
                         text-gray-400 hover:text-white hover:bg-white/10
                         transition-colors duration-200"
              aria-label="Close settings"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-3xl mx-auto">
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
